/**
 * Ballast's ONE upload store (Nick, 2026-07-31, option 2: "one button,
 * one tray").
 *
 * Every upload in the product — footage into an ▲ROLL room, a cut into
 * a PL▲YBACK room, either kind while creating a project, a workspace
 * logo — is queued here and reported by the single docked tray. The
 * store lives above the router, so an upload started in one room keeps
 * running while you walk to Settings, and there is exactly one place to
 * look for "is it done yet".
 *
 * What lives here: the queue, concurrency, progress, pause/resume,
 * retry, and the per-kind mint + finish hooks. What does NOT live here:
 * any opinion about where the file came from. Surfaces hand over a
 * File plus a Job/kind and get out of the way.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { aroll, playback, tenant as tenantApi } from '@/lib/api';
import { uploadToMux, type MuxUploadHandle, type MuxUploadProgress } from '@/lib/muxUpload';
import { registerLocalPreview } from '@/lib/localPreview';
import { readFileStartTimecode } from '@/lib/mp4-timecode';
import { WALLET_CHANGED_EVENT } from '@/components/ArollWalletChip';

/** Fired whenever an upload finishes, so lists can refresh themselves
 *  without the tray knowing what a project list is. */
export const UPLOAD_DONE_EVENT = 'ballast:upload-done';

/** How many files move at once. Two keeps a domestic connection busy
 *  without starving any single upload of bandwidth. */
const CONCURRENCY = 2;
/** How long a finished row lingers in the tray before clearing. */
const SETTLE_MS = 6000;

export type UploadKind = 'aroll' | 'playback' | 'logo';

export type UploadItem = {
  id: string;
  kind: UploadKind;
  /** Project the file belongs to. null for workspace-level uploads
   *  (the logo), which have no project. */
  jobId: string | null;
  /** Playback only: the cut this file lands on. See StartInput. */
  assetId?: string;
  /** What the tray shows: the file's name. */
  name: string;
  sizeBytes: number;
  state: 'queued' | 'uploading' | 'done' | 'failed';
  pct: number;
  detail: MuxUploadProgress | null;
  paused: boolean;
  error?: string;
};

type StartInput = {
  kind: UploadKind;
  jobId: string | null;
  file: File;
  /** Playback only: the cut (ReviewAsset) this file lands on as the
   *  next version. Omitted = the job's default cut, the pre-existing
   *  behaviour. */
  assetId?: string;
  /** Called once the bytes are in, before the row settles. Lets the
   *  caller refresh a specific view; the global event covers the rest. */
  onDone?: () => void;
};

type UploadStore = {
  items: UploadItem[];
  /** Queue one or more files. Returns their ids. */
  start: (inputs: StartInput[]) => string[];
  pause: (id: string) => void;
  resume: (id: string) => void;
  cancel: (id: string) => void;
  retry: (id: string) => void;
  /** Clear finished + failed rows. */
  clearSettled: () => void;
  /** True while anything is queued or moving. */
  busy: boolean;
};

const Ctx = createContext<UploadStore | null>(null);

export function useUploads(): UploadStore {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useUploads must be used inside <UploadProvider>');
  return ctx;
}

export function UploadProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  // Everything the render doesn't need lives in refs, so a progress
  // tick never re-runs the pump.
  const handles = useRef(new Map<string, MuxUploadHandle>());
  const files = useRef(new Map<string, File>());
  const dones = useRef(new Map<string, () => void>());
  const inFlight = useRef(0);
  const settleTimers = useRef(new Map<string, number>());
  const pumpRef = useRef<() => void>(() => {});
  /** Mirror of `items` the pump can read without a state updater. */
  const itemsRef = useRef<UploadItem[]>([]);
  /** Ids already handed to a worker. Guards against a double call in
   *  the same tick claiming one file twice. */
  const claimed = useRef(new Set<string>());

  useEffect(() => {
    const timers = settleTimers.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      timers.clear();
    };
  }, []);

  const patch = useCallback((id: string, next: Partial<UploadItem>) => {
    itemsRef.current = itemsRef.current.map((it) =>
      it.id === id ? { ...it, ...next } : it,
    );
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...next } : it)));
  }, []);

  const settle = useCallback((id: string) => {
    settleTimers.current.set(
      id,
      window.setTimeout(() => {
        settleTimers.current.delete(id);
        files.current.delete(id);
        dones.current.delete(id);
        claimed.current.delete(id);
        itemsRef.current = itemsRef.current.filter((it) => it.id !== id);
        setItems((cur) => cur.filter((it) => it.id !== id));
      }, SETTLE_MS),
    );
  }, []);

  /** Mint the right upload URL for this kind and stream the file. */
  const run = useCallback(
    async (item: UploadItem, file: File) => {
      if (item.kind === 'logo') {
        // Logos are a small multipart POST, not a Mux direct upload, so
        // there is no byte-level progress to report — the row simply
        // sits at "uploading" until the request returns.
        await tenantApi.uploadLogo(file);
        return;
      }
      const mint =
        item.kind === 'playback'
          ? await playback.createUpload(item.jobId as string, {
              ...(item.assetId ? { assetId: item.assetId } : {}),
              filename: file.name,
              mimeType: file.type || 'video/mp4',
              sizeBytes: file.size,
              // The clip's own start TC, so selects and notes export onto
              // the frames the customer's copy carries.
              sourceStartTc: await readFileStartTimecode(file),
            })
          : await aroll.createUpload(
              item.jobId as string,
              file.name,
              null,
              // The clip's own start TC, so exports conform to the
              // editor's copy (a Resolve export is stamped 01:00:00:00).
              await readFileStartTimecode(file),
            );
      const handle = uploadToMux(mint.uploadUrl, file, (pct, detail) =>
        patch(item.id, { pct, detail: detail ?? null }),
      );
      handles.current.set(item.id, handle);
      await handle.done;
    },
    [patch],
  );

  /**
   * Start the next queued file, if there's room.
   *
   * NOTHING here may live inside a setState updater: React StrictMode
   * double-invokes updaters, and the first version of this pumped from
   * inside one — which minted two Mux uploads for a single file and
   * would have billed the encode twice. The queue is read from a ref,
   * the claim is recorded in a ref synchronously (so a second call in
   * the same tick can't take the same item), and only then does state
   * move.
   */
  const pump = useCallback(() => {
    if (inFlight.current >= CONCURRENCY) return;
    const next = itemsRef.current.find(
      (it) => it.state === 'queued' && !claimed.current.has(it.id),
    );
    if (!next) return;
    claimed.current.add(next.id);
    inFlight.current += 1;
    patch(next.id, { state: 'uploading' });

    void (async () => {
      const file = files.current.get(next.id);
      if (!file) {
        patch(next.id, { state: 'failed', error: 'File is no longer available' });
        inFlight.current -= 1;
        pumpRef.current();
        return;
      }
      try {
        await run(next, file);
        patch(next.id, { state: 'done', pct: 100 });
        dones.current.get(next.id)?.();
        window.dispatchEvent(new Event(UPLOAD_DONE_EVENT));
        // The wallet's meters moved; let the chip refetch.
        window.dispatchEvent(new Event(WALLET_CHANGED_EVENT));
        settle(next.id);
      } catch (err) {
        patch(next.id, {
          state: 'failed',
          error: err instanceof Error ? err.message : 'Upload failed',
        });
      } finally {
        handles.current.delete(next.id);
        inFlight.current -= 1;
        pumpRef.current();
      }
    })();
  }, [patch, run, settle]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    pumpRef.current = pump;
  }, [pump]);

  const start = useCallback(
    (inputs: StartInput[]) => {
      const made: UploadItem[] = [];
      for (const input of inputs) {
        if (input.file.size === 0) continue;
        const id = `${input.file.name}-${input.file.size}-${Math.random().toString(36).slice(2, 8)}`;
        files.current.set(id, input.file);
        if (input.onDone) dones.current.set(id, input.onDone);
        // The chosen file is the only copy of the footage we can show
        // until Mux has encoded it, so the room can play it meanwhile.
        if (input.jobId && input.kind !== 'logo') {
          registerLocalPreview(input.jobId, input.file);
        }
        made.push({
          id,
          kind: input.kind,
          jobId: input.jobId,
          ...(input.assetId ? { assetId: input.assetId } : {}),
          name: input.file.name,
          sizeBytes: input.file.size,
          state: 'queued',
          pct: 0,
          detail: null,
          paused: false,
        });
      }
      if (made.length === 0) return [];
      itemsRef.current = [...itemsRef.current, ...made];
      setItems((cur) => [...cur, ...made]);
      // Let the state land before the pump reads it.
      window.setTimeout(() => pumpRef.current(), 0);
      return made.map((m) => m.id);
    },
    [],
  );

  const pause = useCallback(
    (id: string) => {
      handles.current.get(id)?.pause();
      patch(id, { paused: true });
    },
    [patch],
  );

  const resume = useCallback(
    (id: string) => {
      handles.current.get(id)?.resume();
      patch(id, { paused: false });
    },
    [patch],
  );

  const cancel = useCallback((id: string) => {
    handles.current.get(id)?.abort();
    handles.current.delete(id);
    files.current.delete(id);
    dones.current.delete(id);
    claimed.current.delete(id);
    itemsRef.current = itemsRef.current.filter((it) => it.id !== id);
    setItems((cur) => cur.filter((it) => it.id !== id));
  }, []);

  const retry = useCallback(
    (id: string) => {
      claimed.current.delete(id);
      itemsRef.current = itemsRef.current.map((it) =>
        it.id === id ? { ...it, state: 'queued' as const, pct: 0, detail: null } : it,
      );
      patch(id, { state: 'queued', pct: 0, error: undefined, detail: null });
      window.setTimeout(() => pumpRef.current(), 0);
    },
    [patch],
  );

  const clearSettled = useCallback(() => {
    const keep = (it: UploadItem) => it.state === 'queued' || it.state === 'uploading';
    itemsRef.current.filter((it) => !keep(it)).forEach((it) => claimed.current.delete(it.id));
    itemsRef.current = itemsRef.current.filter(keep);
    setItems((cur) => cur.filter(keep));
  }, []);

  const busy = items.some((it) => it.state === 'queued' || it.state === 'uploading');

  // Leaving the page mid-upload loses the bytes: UpChunk lives in this
  // document. The tray keeps uploads alive across ROUTES, not across a
  // page unload, so warn on the one case it can't survive.
  useEffect(() => {
    if (!busy) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [busy]);

  const value = useMemo<UploadStore>(
    () => ({ items, start, pause, resume, cancel, retry, clearSettled, busy }),
    [items, start, pause, resume, cancel, retry, clearSettled, busy],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
